import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Per-location station catalog (Bar / Register / Pastry / Floor / Kitchen
// — manager-defined, not enum). Replace-all save semantics, same shape as
// StaffingPeriodsEditor.
export function StationsEditor({ locationId, currentUser, canEdit }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMsg, setSavingMsg] = useState('');

  async function load() {
    if (!locationId) return;
    setLoading(true);
    try {
      const data = await api('GET', `/api/locations/${locationId}/stations`, null, headers);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { setSavingMsg(`Load failed: ${e.message || e}`); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [locationId]);

  function update(idx, val) { setRows(rs => rs.map((r, i) => i === idx ? { ...r, name: val } : r)); }
  function addRow() { setRows(rs => [...rs, { name: '', sort_order: rs.length }]); }
  function removeRow(idx) { setRows(rs => rs.filter((_, i) => i !== idx)); }
  function presetDefaults() {
    if (rows.length > 0 && !confirm('Replace existing stations with the default set (Bar / Register / Food / Back Bar)?')) return;
    setRows([
      { name: 'Bar',      sort_order: 0 },
      { name: 'Register', sort_order: 1 },
      { name: 'Food',     sort_order: 2 },
      { name: 'Back Bar', sort_order: 3 },
    ]);
  }
  async function save() {
    setSavingMsg('Saving…');
    try {
      const stations = rows.map((r, i) => ({ name: r.name, sort_order: i }));
      await api('PUT', `/api/locations/${locationId}/stations`, { stations }, headers);
      setSavingMsg('Saved.');
      load();
    } catch (e) { setSavingMsg(`Save failed: ${e.message || e}`); }
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div>
          <div className="text-sm font-semibold">Stations</div>
          <div className="text-xs text-gray-500">Floor positions managers assign during a shift (e.g., Bar / Register / Pastry).</div>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button onClick={presetDefaults} className="btn-secondary" style={{ fontSize: 11 }}>Use defaults</button>
            <button onClick={addRow} className="btn-secondary" style={{ fontSize: 11 }}>+ Add station</button>
            <button onClick={save}
              style={{ background: 'var(--forest)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Save
            </button>
          </div>
        )}
      </div>
      {loading ? (
        <div style={{ fontSize: 12, color: '#9e9e9e' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9e9e9e' }}>No stations configured. Click "Use defaults" to start.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 32px', gap: 6, alignItems: 'center' }}>
              <input value={r.name || ''} onChange={e => update(i, e.target.value)} placeholder="Station name" disabled={!canEdit}
                style={{ padding: '6px 10px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 4 }} />
              {canEdit && (
                <button onClick={() => removeRow(i)}
                  style={{ background: '#fff', border: '1px solid #d0d4d9', borderRadius: 4, color: '#dc2626', cursor: 'pointer', fontSize: 16, lineHeight: 1, height: 28 }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
      {savingMsg && (
        <div style={{ fontSize: 11, color: savingMsg.includes('failed') ? '#dc2626' : '#16a34a', marginTop: 6 }}>{savingMsg}</div>
      )}
    </div>
  );
}
